// MUST stay the first import: polyfills Promise.prototype.finally for very
// old browsers (Android 4–5 WebViews) before any module that uses .finally
// evaluates — otherwise the SPA crashes pre-mount for those visitors (#7103).
import "./lib/promise-finally-polyfill";
import "./lib/sentry";
import { i18nReady } from "./i18n";

// iOS Safari in Lockdown Mode / "Block All Cookies" throws a SecurityError on
// ANY access to window.sessionStorage — including the property read itself, not
// just get/set. Several third-party libraries touch it during boot (e.g.
// @supabase/phoenix reads `global.sessionStorage` while constructing the
// realtime client), so the crash happens deep inside their code where we can't
// add a try/catch. If the native store is unreachable, shadow it on `window`
// with an in-memory shim so those reads degrade gracefully. Only installed when
// the native store actually throws; normal browsers are left untouched.
if (typeof window !== "undefined") {
  // Only treat access-denied failures (SecurityError) as "store unreachable".
  // A QuotaExceededError in a normal browser (storage full) must NOT trigger
  // the shim — that would silently shadow a perfectly good native store.
  const isAccessDenied = (err: unknown): boolean => {
    if (typeof DOMException !== "undefined" && err instanceof DOMException) {
      return err.name === "SecurityError" || err.code === 18;
    }
    return err instanceof Error && /security/i.test(err.name);
  };
  try {
    const probe = "__ss_probe__";
    window.sessionStorage.setItem(probe, "1");
    window.sessionStorage.removeItem(probe);
  } catch (err) {
    // Only swap in the shim for access-denied failures. Any other error
    // (e.g. a transient QuotaExceededError) leaves the native store in place.
    if (isAccessDenied(err)) {
      try {
        const store = new Map<string, string>();
        const shim = {
          get length() {
            return store.size;
          },
          clear() {
            store.clear();
          },
          getItem(key: string) {
            return store.has(key) ? store.get(key)! : null;
          },
          key(index: number) {
            return Array.from(store.keys())[index] ?? null;
          },
          removeItem(key: string) {
            store.delete(key);
          },
          setItem(key: string, value: string) {
            store.set(key, String(value));
          },
        } as Storage;
        Object.defineProperty(window, "sessionStorage", {
          value: shim,
          configurable: true,
        });
      } catch {
        // If we can't shadow it either, the guarded reads below still no-op.
      }
    }
  }
}

// Auto-recover from stale Vite chunk hashes after a deploy. When the CDN
// no longer has the chunk an old tab is trying to lazy-load, Safari throws
// "TypeError: Load failed" and Chrome throws "ChunkLoadError". We force a
// one-shot reload so the tab picks up the fresh index.html / chunk hashes
// instead of surfacing the error to the user (and to Sentry).
if (typeof window !== "undefined") {
  const RELOAD_FLAG = "vite-preload-reloaded";
  // Guarded sessionStorage helpers — even with the shim above, defining the
  // shadow property can itself fail in some locked-down contexts, so our own
  // reads/writes stay defensive.
  const ssGet = (key: string): string | null => {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  };
  const ssSet = (key: string, value: string): void => {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      /* ignore — storage unavailable */
    }
  };
  const ssRemove = (key: string): void => {
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* ignore — storage unavailable */
    }
  };
  const tryRecover = (event?: Event): void => {
    if (ssGet(RELOAD_FLAG)) return;
    ssSet(RELOAD_FLAG, "1");
    if (event) event.preventDefault();
    window.location.reload();
  };
  // Vite resolves a failed import to undefined when this event is cancelled.
  // Schedule recovery without cancelling it: eager loaders must still reject
  // with the original failure, never dereference an undefined module.
  window.addEventListener("vite:preloadError", () => tryRecover());
  // Belt-and-braces: catch unhandled "Loading chunk … failed" / "Failed to
  // fetch dynamically imported module" errors that some browsers raise
  // before the vite:preloadError event.
  window.addEventListener("error", (e) => {
    // ErrorEvent does not promise a rejection-style reason. Preserve support
    // for that optional extension without changing which errors trigger reload.
    const reason = "reason" in e ? e.reason : undefined;
    const reasonMessage = reason && typeof reason === "object" && "message" in reason
      ? reason.message : undefined;
    const msg = String(reason instanceof Error ? reason.message : reasonMessage || reason || e.message || "");
    if (/Loading chunk|dynamically imported module|Load failed|Failed to fetch dynamically/i.test(msg)) {
      tryRecover(e);
    }
  });
  window.addEventListener("unhandledrejection", (e) => {
    const msg = String((e.reason && (e.reason.message || e.reason)) || "");
    if (/Loading chunk|dynamically imported module|Load failed|Failed to fetch dynamically/i.test(msg)) {
      tryRecover(e);
    }
    if (/lock was stolen/i.test(msg)) {
      e.preventDefault();
    }
  });
  // Clear the guard once the page has booted successfully so future stale-
  // chunk events can also recover.
  window.addEventListener("load", () => {
    ssRemove(RELOAD_FLAG);
  });
}

import { init as initAnalytics } from "./lib/analytics";
import { captureProductionPostHogBeacon } from "./lib/analytics/environment";
import { scheduleAnalyticsStartup } from "./lib/analytics/startup";
import { captureFirstTouch } from "./lib/analytics/utm";
import { initAcquisition } from "./lib/analytics/acquisition";
import { initRequestAcquisitionActivity } from "./lib/analytics/request-acquisition";
import { bootIntercom, ensureIntercomConfigured, isIntercomConfigured } from "./lib/intercom";
import { subscribeConsent } from "./lib/analytics/consent";

// Task #7081 (INP): third-party boot — the PostHog/GA SDK loads inside
// initAnalytics() and the Intercom widget script — used to run synchronously
// at module evaluation, competing with the user's earliest interactions.
// Defer third-party boot. Analytics waits for interaction or a short dwell;
// Intercom keeps the idle callback. Everything time-sensitive is
// captured synchronously first:
//   - first-touch UTM/referrer attribution reads the ORIGINAL landing URL,
//     so it must run before the SPA router can rewrite it;
//   - pre-mount PostHog beacons (below) post straight to the ingest proxy
//     and never depended on the SDK;
//   - a support CTA clicked before the idle boot self-configures Intercom
//     on demand via ensureIntercomConfigured() inside openSupportChat().
captureFirstTouch();
initAcquisition();
initRequestAcquisitionActivity();

function deferToIdle(fn: () => void, timeoutMs = 2500): void {
  type IdleWindow = Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  };
  const w = window as IdleWindow;
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(fn, { timeout: timeoutMs });
  } else {
    window.setTimeout(fn, 200);
  }
}

scheduleAnalyticsStartup((reason) => {
  initAnalytics(reason);
});
deferToIdle(() => {
  // Pull the public Intercom workspace id from the platform config endpoint.
  // Best-effort: if this fails the Messenger simply stays disabled (support
  // CTAs retry the fetch on demand, then fall back to mailto).
  void ensureIntercomConfigured().then((ok) => {
    if (!ok) return;
    // Boot the Messenger for EVERYONE so the launcher is always visible,
    // regardless of cookie choice. It starts in privacy-light mode (no
    // identity/traits/tracking) and upgrades to the full identified
    // experience once analytics consent is granted (logged-in users are
    // re-booted with verified identity by AuthProvider).
    void bootIntercom();
  });
});
subscribeConsent(() => {
  if (!isIntercomConfigured()) return;
  // Reconcile in place on any consent change: granting analytics upgrades
  // Intercom to the identified/tracked experience; revoking it downgrades
  // back to privacy-light (the launcher stays visible — we never shut it
  // down/hide it). bootIntercom() replays the cached identity when allowed
  // and strips it back to anonymous when not.
  void bootIntercom();
});
import { createRoot, hydrateRoot } from "react-dom/client";
import App from "./App";
import { installTranslationDomGuard } from "./lib/dom-translation-guard";
import { isChunkLoadError } from "./lib/lazyRetry";
import { COUNTRY_ROOT_ATTR, PUBLIC_PAGE_ATTR, NOT_FOUND_ATTR, SsrRequestContext, subscribePublicHydration, isPublicPageHydrated, useSsrBootstrap, type SsrRequest } from "./lib/ssr-bootstrap";
import { countryHydrationSnapshot } from "./lib/country-hydration-snapshot.mjs";
import { hydrate } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { setPlatformOverride } from "./lib/platform/config";
import { useSyncExternalStore } from "react";
import { rememberCanonicalPayload } from "../../../lib/copy/src/public-canonical-client.mjs";
import { prepareCanonicalBootstrap, installCanonicalApiTransport } from "./lib/canonical-transport";
import { installReleaseFreshnessGuard } from "./lib/release-freshness.mjs";

installCanonicalApiTransport();
installReleaseFreshnessGuard({ baseUrl: import.meta.env.BASE_URL || "/" });

function PublicHydrationApp({ snapshot }: { snapshot: { pathname: string; flags: Record<string, boolean> } }) {
  const request = useSyncExternalStore(subscribePublicHydration, () => isPublicPageHydrated() ? null : snapshot, () => snapshot);
  return <SsrRequestContext.Provider value={request}><App /></SsrRequestContext.Provider>;
}
function CountryHydrationApp({ snapshot }: { snapshot: SsrRequest }) {
  // Match the server's request-owned country/dates during hydration only.
  // App's existing bootstrap completion then restores client preferences.
  const bootstrapping = useSsrBootstrap();
  return <SsrRequestContext.Provider value={bootstrapping ? snapshot : null}><App /></SsrRequestContext.Provider>;
}
import { setEagerHomePage } from "./lib/home-route";
import { loadEagerPublicPages, loadEagerNotFoundPage } from "./lib/public-page-routes";
import "./index.css";
// Country homepages already contain rendered hero markup before hydration.
// Load its scoped styles with the entry CSS, not the lazy HomePage chunk:
// applying them later changes the hero height and re-crops its cover image.
import "./pages/HomePage.css";

// Make React's DOM reconciliation resilient to in-browser translation engines
// before anything mounts. Without this, translated pages throw
// "NotFoundError: The object can not be found here." which cascades into a
// "RangeError: Maximum call stack size exceeded" and crashes the page.
installTranslationDomGuard();

// ─────────────────────────────────────────────────────────────────────────────
// Task #6961 — mount watchdog telemetry + pre-mount recovery.
//
// Users tapping email/SMS links landed on a blank page whenever the pre-mount
// locale-chunk import stalled or failed (i18nReady never resolved → React never
// mounted). i18nReady now fails open after a bounded wait, and here we:
//   1. classify how this visit arrived (email/SMS vs direct vs web referral),
//   2. fire a lightweight PostHog beacon when the shell is served but the app
//      hasn't mounted within a threshold ("app_mount_stalled") and when it
//      finally does after that ("app_mount_recovered"), plus a beacon whenever
//      the i18n fail-open path was taken — so the blast radius is measurable,
//   3. route a REJECTED locale-chunk import through the same one-shot reload
//      recovery used for lazy routes (stale chunk hashes after a deploy),
//      guarded by a TTL flag so a user can never be stranded in a reload loop.
// ─────────────────────────────────────────────────────────────────────────────
const MOUNT_STALL_THRESHOLD_MS = 8_000;
const bootStartedAt = Date.now();

type ReferrerType = "email_or_sms" | "search" | "web" | "direct";

function classifyReferrer(): ReferrerType {
  try {
    const params = new URLSearchParams(window.location.search);
    const medium = (params.get("utm_medium") || "").toLowerCase();
    if (/email|sms|message/.test(medium)) return "email_or_sms";
    const path = window.location.pathname;
    // Tokenised deep links are only ever distributed via email/SMS.
    if (/\/(win-back|probe-respond|payout-link|bookings\/claim)\//.test(path)) {
      return "email_or_sms";
    }
    const ref = document.referrer;
    if (!ref) return "direct";
    if (/google\.|bing\.|duckduckgo\.|yahoo\./i.test(ref)) return "search";
    return "web";
  } catch {
    return "direct";
  }
}

/**
 * Consent-exempt diagnostic beacon straight to the PostHog ingest proxy.
 * The full analytics SDK only loads after consent — far too late for a page
 * that never mounts — so this posts a minimal anonymous capture payload with
 * `keepalive` (survives an imminent reload/navigation). Best-effort: never
 * throws, never blocks boot.
 */
function sendMountBeacon(event: string, props: Record<string, unknown>): void {
  captureProductionPostHogBeacon(event, {
    distinct_id: "pre-mount-watchdog",
    $process_person_profile: false,
    locale: document.documentElement.lang || "en",
    path: window.location.pathname,
    referrer_type: classifyReferrer(),
    elapsed_ms: Date.now() - bootStartedAt,
    ...props,
  });
}

let mounted = false;
const stallTimer = setTimeout(() => {
  if (!mounted) sendMountBeacon("app_mount_stalled", {});
}, MOUNT_STALL_THRESHOLD_MS);

// One-shot pre-mount reload guard with a TTL (mirrors lazyRetry's per-chunk
// guard). A plain boolean flag cleared on `load` could race the locale import
// on the reloaded page and re-arm a reload loop; the TTL makes a repeat
// failure within the window fail OPEN to English instead of reloading again.
const I18N_RELOAD_GUARD_KEY = "i18n-preload-reloaded";
const I18N_RELOAD_GUARD_TTL_MS = 60_000;

function i18nReloadGuardActive(): boolean {
  try {
    const raw = sessionStorage.getItem(I18N_RELOAD_GUARD_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts) || Date.now() - ts > I18N_RELOAD_GUARD_TTL_MS) {
      sessionStorage.removeItem(I18N_RELOAD_GUARD_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

async function mountApp(): Promise<void> {
  if (mounted) return;
  mounted = true;
  clearTimeout(stallTimer);
  const rootEl = document.getElementById("root")!;
  // Only documents explicitly owned by the shared country-root SSR renderer
  // (marker emitted by serve-static) are hydrated; every other page keeps
  // createRoot so unrelated routes behave exactly as before.
  if (rootEl.hasAttribute(PUBLIC_PAGE_ATTR)) {
    const data = document.getElementById("public-page-ssr-data");
    if (!data?.textContent) throw new Error("Public SSR document has no hydration snapshot");
    const snapshot = JSON.parse(data.textContent);
    rememberCanonicalPayload(snapshot);
    await prepareCanonicalBootstrap(snapshot, snapshot.pathname || window.location.pathname);
    if (snapshot.language) {
      const { default: publicI18n, addLanguageResources } = await import("./i18n");
      await addLanguageResources(snapshot.language);
      await publicI18n.changeLanguage(snapshot.language);
    }
    hydrate(queryClient, snapshot.dehydratedState);
    setPlatformOverride(snapshot.platform || (snapshot.flags.brand_cutover_driveshare ? "driveshare" : null));
    await loadEagerPublicPages();
    hydrateRoot(rootEl, <PublicHydrationApp snapshot={snapshot} />, {
      onRecoverableError(error) { console.error("[public-page] hydration recovery", error); },
    });
  } else if (rootEl.hasAttribute(COUNTRY_ROOT_ATTR)) {
    const seedNode = document.getElementById("landing-prerender-data");
    const notFoundPath = rootEl.getAttribute(NOT_FOUND_ATTR);
    if (!seedNode?.textContent && notFoundPath === null)
      throw new Error("Country SSR document has no hydration snapshot");
    const seed = notFoundPath === null ? JSON.parse(seedNode!.textContent!) : null;
    const snapshot = countryHydrationSnapshot(seed, window.location.pathname, notFoundPath);
    if (!snapshot.notFound) await prepareCanonicalBootstrap(seed, window.location.pathname);
    if (snapshot.flags) setPlatformOverride(snapshot.flags.brand_cutover_driveshare ? "driveshare" : null);
    // The homepage module must be loaded before hydration so the first client
    // render is synchronous and identical to the server markup.
    if (snapshot.notFound) {
      await loadEagerNotFoundPage();
    } else try {
      const home = await import("./pages/HomePage");
      setEagerHomePage(home.default);
    } catch (error) {
      if (isChunkLoadError(error)) { window.location.reload(); return; }
      throw error;
    }
    hydrateRoot(rootEl, <CountryHydrationApp snapshot={snapshot} />, {
      onRecoverableError(error, info) {
        console.error("[country-home] hydration recovery", error, {
          componentStack: info.componentStack,
        });
      },
    });
  } else {
    // Legacy saved pages also synchronously seed car-card queries, despite not
    // owning a React SSR marker. Prepare their server-resolved identities before
    // the first render, just as for public and country SSR documents.
    const seedNode = document.getElementById("landing-prerender-data");
    if (seedNode?.textContent) {
      await prepareCanonicalBootstrap(JSON.parse(seedNode.textContent), window.location.pathname);
    }
    createRoot(rootEl).render(<App />);
  }
  const elapsed = Date.now() - bootStartedAt;
  if (elapsed > MOUNT_STALL_THRESHOLD_MS) {
    sendMountBeacon("app_mount_recovered", {});
  }
}

// Defer React mount until the initial language bundle is registered so there
// is no flash-of-English when the user's detected language is not en — but
// NEVER indefinitely: i18nReady fails open after a bounded wait (Task #6961),
// and this handler always mounts (English fallback) on any degraded outcome.
i18nReady
  .then((outcome) => {
    if (outcome.ok) {
      mountApp();
      return;
    }
    if (
      outcome.reason === "error" &&
      isChunkLoadError(outcome.error) &&
      !i18nReloadGuardActive()
    ) {
      // A stale/missing locale chunk (hash rotated by a deploy). One-shot
      // reload picks up the fresh index.html + chunk hashes — same recovery
      // lazy routes get, now covering the pre-mount path.
      sendMountBeacon("i18n_preload_reload", { reason: outcome.reason });
      try {
        sessionStorage.setItem(I18N_RELOAD_GUARD_KEY, String(Date.now()));
      } catch {
        /* storage unavailable — fall through to mounting with English */
      }
      window.location.reload();
      return;
    }
    // Fail open: mount now with English fallback; the background retry /
    // in-flight load in i18n/index.ts upgrades the language in place.
    sendMountBeacon("i18n_fail_open", { reason: outcome.reason });
    mountApp();
  })
  .catch(() => {
    // i18nReady is designed never to reject, but the app mounting must not
    // hinge on that invariant holding forever.
    mountApp();
  });
